import { useCallback, useEffect, useMemo, useState } from "react";
import { Gift, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { VerifiedName } from "../../components/VerifiedBadge";

const defaultSettings = {
  enabled: true,
  partition_amount: 20,
  under_partition_percent: 40,
  over_partition_percent: 50,
  cross_club_restriction: true,
};

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const unitsToMoney = (value) => `$${((Number(value) || 0) / 1000).toFixed(2)}`;
const percent = (value) => `${Number(value || 0).toFixed(2)}%`;

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-label={label}
      className={`relative h-8 w-14 rounded-full transition-colors ${
        checked ? "bg-[#2F6FA5]" : "bg-white/10"
      }`}
    >
      <span
        className={`absolute top-1 h-6 w-6 rounded-full bg-black transition-transform ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold">{label}</span>
      {children}
      {hint && <span className="mt-2 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

export default function AdminClientReferrals() {
  const [settings, setSettings] = useState(defaultSettings);
  const [preview, setPreview] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/client-referrals");
    setSettings({ ...defaultSettings, ...(data.settings || {}) });
    setPreview(data.preview || []);
    setBonuses(data.bonuses || []);
  }, []);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [load]);

  const localPreview = useMemo(() => {
    const partition = Number(settings.partition_amount || 0);
    const samples = preview.length ? preview : [
      { deposit: 10 },
      { deposit: 15 },
      { deposit: 20 },
      { deposit: 21 },
      { deposit: 50 },
      { deposit: 100 },
    ];
    return samples.map((row) => {
      const deposit = Number(row.deposit || 0);
      const under = deposit <= partition;
      const bonusPercent = under
        ? Number(settings.under_partition_percent || 0)
        : Number(settings.over_partition_percent || 0);
      return {
        deposit,
        tier: under ? "<= partition" : "> partition",
        percent: bonusPercent,
        bonus: Number((deposit * bonusPercent / 100).toFixed(2)),
      };
    });
  }, [preview, settings]);

  const update = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        enabled: Boolean(settings.enabled),
        partition_amount: Number(settings.partition_amount || 0),
        under_partition_percent: Number(settings.under_partition_percent || 0),
        over_partition_percent: Number(settings.over_partition_percent || 0),
        cross_club_restriction: Boolean(settings.cross_club_restriction),
      };
      const { data } = await api.put("/admin/client-referrals/settings", payload);
      setSettings({ ...defaultSettings, ...(data.settings || {}) });
      setPreview(data.preview || []);
      setBonuses(data.bonuses || []);
      toast.success("Referral bonus settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Gift className="h-6 w-6 text-[#2F6FA5]" />
          <h1 className="text-2xl font-semibold tracking-tight">Client Referral Bonus</h1>
        </div>
        <p className="mt-1 max-w-4xl text-sm text-slate-400">
          Reward members for inviting friends. The configured bonus is credited as Freeplay after the friend's first approved real-money deposit. On eligible redemptions, 60% is deducted and 40% is credited to the wallet. Referrers must have a verified phone number.
        </p>
      </div>

      <form onSubmit={save} className="mb-6 rounded-xl border border-[#2F6FA5]/25 bg-[#121214] p-4 sm:p-6">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Enable referral bonus for this club</h2>
            <p className="mt-1 text-sm text-slate-400">When OFF, no bonus is paid even if a referee loads a game.</p>
          </div>
          <Toggle checked={settings.enabled} onChange={(value) => update("enabled", value)} label="Enable referral bonus" />
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <Field label="Partition amount ($)" hint='Deposits <= this amount use the "below" percent.'>
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.partition_amount}
              onChange={(e) => update("partition_amount", e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="<= partition: bonus %">
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.under_partition_percent}
              onChange={(e) => update("under_partition_percent", e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="> partition: bonus %">
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.over_partition_percent}
              onChange={(e) => update("over_partition_percent", e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="my-6 border-t border-white/10" />

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#2F6FA5]" />
              <h2 className="text-lg font-semibold">Anti-abuse: cross-club restriction</h2>
            </div>
            <p className="mt-1 max-w-3xl text-sm text-slate-400">
              When ON, a referred player's first qualifying game load can only pay one referral bonus record.
            </p>
          </div>
          <Toggle checked={settings.cross_club_restriction} onChange={(value) => update("cross_club_restriction", value)} label="Cross-club restriction" />
        </div>

        <div className="mt-6 flex justify-end">
          <button
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2F6FA5] px-6 py-3 text-sm font-bold text-white hover:bg-[#4E91C8] disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      </form>

      <section className="mb-6 rounded-xl border border-[#2F6FA5]/25 bg-[#121214] p-4 sm:p-6">
        <h2 className="text-lg font-semibold">Bonus preview</h2>
        <p className="mt-1 text-sm text-slate-400">Live calculation based on the values above.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-[700px] w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
              <tr className="border-b border-white/10">
                <th className="py-3 pr-4">Deposit</th>
                <th className="py-3 pr-4">Tier</th>
                <th className="py-3 pr-4">Bonus %</th>
                <th className="py-3 text-right">Referrer earns</th>
              </tr>
            </thead>
            <tbody>
              {localPreview.map((row) => (
                <tr key={`${row.deposit}-${row.tier}`} className="border-b border-white/5 last:border-0">
                  <td className="py-3 pr-4 tabular-nums">{money(row.deposit)}</td>
                  <td className="py-3 pr-4">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      row.tier === "<= partition" ? "bg-white/10 text-white" : "bg-[#2F6FA5] text-white"
                    }`}>
                      {row.tier}
                    </span>
                  </td>
                  <td className="py-3 pr-4 tabular-nums">{percent(row.percent)}</td>
                  <td className="py-3 text-right font-semibold tabular-nums text-[#2F6FA5]">{money(row.bonus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-[#2F6FA5]/25 bg-[#121214] p-4 sm:p-6">
        <h2 className="text-lg font-semibold">Recent referral bonuses paid in this club</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-[900px] w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
              <tr className="border-b border-white/10">
                <th className="py-3 pr-4">When</th>
                <th className="py-3 pr-4">Referrer</th>
                <th className="py-3 pr-4">Referee</th>
                <th className="py-3 pr-4 text-right">Deposit</th>
                <th className="py-3 pr-4 text-right">%</th>
                <th className="py-3 text-right">Bonus</th>
              </tr>
            </thead>
            <tbody>
              {bonuses.map((row) => (
                <tr key={row.id} className="border-b border-white/5 last:border-0">
                  <td className="py-3 pr-4 text-slate-300">{row.created_at ? new Date(row.created_at).toLocaleString() : "-"}</td>
                  <td className="py-3 pr-4">
                    <div className="font-semibold"><VerifiedName name={row.username || row.email || row.user_id || "-"} user={row} /></div>
                    {row.email && <div className="text-xs text-slate-500">{row.email}</div>}
                  </td>
                  <td className="py-3 pr-4">
                    <div className="font-semibold">{row.source_username || row.source_email || row.source_user_id || "-"}</div>
                    {row.source_email && <div className="text-xs text-slate-500">{row.source_email}</div>}
                  </td>
                  <td className="py-3 pr-4 text-right tabular-nums">{unitsToMoney(row.qualifying_wallet_load_amount)}</td>
                  <td className="py-3 pr-4 text-right tabular-nums">{percent(row.referral_bonus_percent)}</td>
                  <td className="py-3 text-right font-semibold tabular-nums text-[#2F6FA5]">{unitsToMoney(row.amount)}</td>
                </tr>
              ))}
              {!loading && bonuses.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">No referral bonuses paid yet.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">Loading referral bonuses...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const inputClass = "w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm outline-none focus:border-[#2F6FA5]";
